import { useImporterViewModel } from '../ImporterViewModel'
import { useMemo, useState } from 'react'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { ColumnDef } from '@tanstack/react-table'
import { Tooltip } from 'components/Tooltip/tooltip'
import { FbLink } from 'components/FbUI/FbLink'
import FbButton from 'components/FbUI/FbButton'
import { oppTypesMap } from 'utils/oppTypesMap'
import { DetailedImportView } from './DetailedImportView'

export function ImportWizardStartImportStep({
  show,
  onImportTags,
  onImportDeals,
  onImportContacts,
  onImportNotes,
}: {
  show: boolean
  onImportTags: () => void
  onImportDeals: () => void
  onImportContacts: () => void
  onImportNotes: () => void
}) {
  const viewModel = useImporterViewModel()
  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary')

  const totalItems = useMemo(() => {
    const c = viewModel.importCounts
    return {
      total:
        c.ugcs.total +
        c.tags.total +
        c.deals.total +
        c.contacts.total +
        c.notes.total,
      imported:
        c.ugcs.imported +
        c.tags.imported +
        c.deals.imported +
        c.contacts.imported +
        c.notes.imported,
    }
  }, [viewModel.importCounts])

  // Define columns for the preview table
  const columnDefs = useMemo<ColumnDef<any>[]>(() => {
    return [
      {
        header: 'Company',
        accessorKey: 'companyName',
        cell: ({ row }) => {
          const { companyName, isCustomCompany, firstbiteId } = row.original

          if (!firstbiteId) {
            return (
              <div className="font-semibold text-gray-900">
                {companyName}
                {isCustomCompany && (
                  <span className="ml-2 text-xs text-gray-500">(UGC)</span>
                )}
              </div>
            )
          }

          const oppType = firstbiteId.substring(
            0,
            2
          ) as keyof typeof oppTypesMap
          const chainPageUrl = oppTypesMap[oppType]?.chainPageUrl ?? '/company/'
          const url = `${chainPageUrl}${firstbiteId}`

          return (
            <div className="font-semibold">
              <FbLink to={url}>{companyName}</FbLink>
              {isCustomCompany && (
                <span className="ml-2 text-xs text-gray-500">(UGC)</span>
              )}
            </div>
          )
        },
      },

      {
        header: 'Tags',
        accessorKey: 'tags',
        size: 60,
        cell: ({ row }) => {
          const tags = row.original.tags || []
          if (tags.length === 0) {
            return <span className="text-gray-400">-</span>
          }
          return (
            <Tooltip
              placement="right"
              content={
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-new-company-pistachio-light text-fb-dark-green text-xs rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              }
              wrapTrigger={false}
            >
              <span className="text-[#087443] hover:text-[#83b9a1] cursor-help font-medium transition-colors">
                {tags.length}x
              </span>
            </Tooltip>
          )
        },
      },

      {
        header: 'Deals',
        accessorKey: 'deals',
        size: 60,
        cell: ({ row }) => {
          const deals = row.original.deals || []
          if (deals.length === 0) {
            return <span className="text-gray-400">-</span>
          }
          return (
            <Tooltip
              placement="right"
              content={
                <div className="flex flex-col gap-2">
                  {deals.map((deal: any, index: number) => (
                    <DealCard key={index} deal={deal} />
                  ))}
                </div>
              }
              wrapTrigger={false}
            >
              <span className="text-[#087443] hover:text-[#83b9a1] cursor-help font-medium transition-colors">
                {deals.length}x
              </span>
            </Tooltip>
          )
        },
      },

      {
        header: 'Notes',
        accessorKey: 'notes',
        size: 60,
        cell: ({ row }) => {
          const notes = row.original.notes || []
          if (notes.length === 0) {
            return <span className="text-gray-400">-</span>
          }
          return (
            <Tooltip
              placement="right"
              content={
                <div className="flex flex-wrap gap-2">
                  {notes.map((note: string, index: number) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                    >
                      {note}
                    </span>
                  ))}
                </div>
              }
              wrapTrigger={false}
            >
              <span className="text-[#087443] hover:text-[#83b9a1] cursor-help font-medium transition-colors">
                {notes.length}x
              </span>
            </Tooltip>
          )
        },
      },

      {
        header: 'Contacts',
        accessorKey: 'contacts',
        size: 60,
        cell: ({ row }) => {
          const contacts = row.original.contacts || []
          if (contacts.length === 0) {
            return <span className="text-gray-400">-</span>
          }
          return (
            <Tooltip
              placement="right"
              content={
                <div className="flex flex-col gap-2">
                  {contacts.map((contact: any, index: number) => (
                    <ContactCard key={index} contact={contact} />
                  ))}
                </div>
              }
              wrapTrigger={false}
            >
              <span className="text-[#087443] hover:text-[#83b9a1] cursor-help font-medium transition-colors">
                {contacts.length}x
              </span>
            </Tooltip>
          )
        },
      },
    ]
  }, [viewModel.importCounts])

  if (!show) {
    return null
  }

  const c = viewModel.importCounts

  return (
    <>
      {/* Progress Summary and View Mode Toggle */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span
            className={
              totalItems.imported === totalItems.total
                ? 'text-green-600 font-medium'
                : ''
            }
          >
            Total: {totalItems.imported}/{totalItems.total}
          </span>
          <span className="text-gray-300">|</span>
          <span>
            UGCs: {c.ugcs.imported}/{c.ugcs.total}
          </span>
          <span className="flex items-center gap-1">
            Tags: {c.tags.imported}/{c.tags.total}
            <FbButton
              size="xs"
              variant="primary-outline"
              onClick={onImportTags}
              loading={viewModel.isSettingTags}
              disabled={c.tags.imported === c.tags.total || c.tags.total === 0}
            >
              Import
            </FbButton>
          </span>
          <span className="flex items-center gap-1">
            Deals: {c.deals.imported}/{c.deals.total}
            <FbButton
              size="xs"
              variant="primary-outline"
              onClick={onImportDeals}
              loading={viewModel.isSettingDeals}
              disabled={
                c.deals.imported === c.deals.total || c.deals.total === 0
              }
            >
              Import
            </FbButton>
          </span>
          <span className="flex items-center gap-1">
            Contacts: {c.contacts.imported}/{c.contacts.total}
            <FbButton
              size="xs"
              variant="primary-outline"
              onClick={onImportContacts}
              loading={viewModel.isSettingContacts}
              disabled={
                c.contacts.imported === c.contacts.total ||
                c.contacts.total === 0
              }
            >
              Import
            </FbButton>
          </span>
          <span className="flex items-center gap-1">
            Notes: {c.notes.imported}/{c.notes.total}
            <FbButton
              size="xs"
              variant="primary-outline"
              onClick={onImportNotes}
              loading={viewModel.isSettingCompanyNotes}
              disabled={
                c.notes.imported === c.notes.total || c.notes.total === 0
              }
            >
              Import
            </FbButton>
          </span>
        </div>
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            onClick={() => setViewMode('summary')}
            className={`
              px-4 py-2 text-sm font-medium border
              ${
                viewMode === 'summary'
                  ? 'bg-fb-dark-green text-white border-fb-dark-green'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }
              rounded-l-lg
            `}
          >
            Summary
          </button>
          <button
            type="button"
            onClick={() => setViewMode('detailed')}
            className={`
              px-4 py-2 text-sm font-medium border-t border-b border-r
              ${
                viewMode === 'detailed'
                  ? 'bg-fb-dark-green text-white border-fb-dark-green'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }
              rounded-r-lg
            `}
          >
            Detailed
          </button>
        </div>
      </div>

      {/* Summary View */}
      {viewMode === 'summary' && (
        <ClientDataTable
          data={viewModel.importSummaryData}
          columns={columnDefs}
          height={'100%'}
          enableSorting
        />
      )}

      {/* Detailed View */}
      {viewMode === 'detailed' && (
        <DetailedImportView show={true} viewModel={viewModel} />
      )}
    </>
  )
}

// Deal Card Component
function DealCard({ deal }: { deal: Record<string, any> }) {
  const hasAnyData =
    deal.deal_sales_stage || deal.deal_account_owner || deal.deal_product

  if (!hasAnyData) {
    return (
      <div className="border border-gray-300 rounded p-2 bg-gray-100">
        <div className="text-xs text-gray-600">
          Deal with no additional details
        </div>
      </div>
    )
  }

  return (
    <div className="border border-gray-300 rounded p-2 bg-white">
      <div className="flex flex-col gap-1 text-xs">
        {deal.deal_sales_stage && (
          <div className="flex items-start gap-1.5">
            <span className="text-gray-600 font-medium min-w-[50px]">
              Stage:
            </span>
            <span className="text-gray-900">{deal.deal_sales_stage}</span>
          </div>
        )}
        {deal.deal_account_owner && (
          <div className="flex items-start gap-1.5">
            <span className="text-gray-600 font-medium min-w-[50px]">
              Owner:
            </span>
            <span className="text-gray-900">{deal.deal_account_owner}</span>
          </div>
        )}
        {deal.deal_product && (
          <div className="flex items-start gap-1.5">
            <span className="text-gray-600 font-medium min-w-[50px]">
              Product:
            </span>
            <span className="text-gray-900">{deal.deal_product}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// Contact Card Component
function ContactCard({ contact }: { contact: Record<string, any> }) {
  const hasAnyData =
    contact.contact_full_name ||
    contact.contact_first_name ||
    contact.contact_last_name ||
    contact.contact_title ||
    contact.contact_work_email ||
    contact.contact_personal_email ||
    contact.contact_generic_email ||
    contact.contact_phone ||
    contact.contact_linkedin_url ||
    contact.contact_label

  if (!hasAnyData) {
    return (
      <div className="border border-gray-300 rounded p-2 bg-gray-100">
        <div className="text-xs text-gray-600">
          Contact with no additional details
        </div>
      </div>
    )
  }

  return (
    <div className="border border-gray-300 rounded p-2 bg-white">
      <div className="flex flex-col gap-1 text-xs">
        {(contact.contact_full_name ||
          contact.contact_first_name ||
          contact.contact_last_name) && (
          <div className="font-semibold text-gray-900">
            {contact.contact_full_name ||
              `${contact.contact_first_name || ''} ${contact.contact_last_name || ''}`.trim()}
          </div>
        )}
        {contact.contact_title && (
          <div className="text-gray-600">{contact.contact_title}</div>
        )}
        {(contact.contact_work_email ||
          contact.contact_personal_email ||
          contact.contact_generic_email) && (
          <div className="text-gray-700">
            {contact.contact_work_email ||
              contact.contact_personal_email ||
              contact.contact_generic_email}
          </div>
        )}
        {contact.contact_phone && (
          <div className="text-gray-700">{contact.contact_phone}</div>
        )}
        {contact.contact_linkedin_url && (
          <div className="text-fb-dark-green truncate max-w-[200px]">
            {contact.contact_linkedin_url}
          </div>
        )}
        {contact.contact_label && (
          <div className="text-gray-500">Label: {contact.contact_label}</div>
        )}
      </div>
    </div>
  )
}
